import {useState, useEffect, useRef} from 'react';
// @ts-expect-error @headlessui/react incompatibility with node16 resolution
import {Transition} from '@headlessui/react';

import type {SanityPageMenuLinkItem} from '~/types/sanityTypes';

import {InternalLink, IconChevron} from '~/components';

type pageMenuState = {
  activePath: SanityPageMenuLinkItem[];
};

export function Navigation({
  links,
  menuSide,
  mobile = false,
  setIsOpen,
}: {
  menuSide: string;
  links: SanityPageMenuLinkItem[];
  mobile: boolean;
  setIsOpen?: (isOpen: boolean) => void;
}) {
  const [state, setState] = useState<pageMenuState>({
    activePath: [],
  });

  const updateState = (newState: pageMenuState) => {
    setState({...state, ...newState});
  };

  return (
    <nav className={`flex w-full ${menuSide}`}>
      <ul className="flex gap-4 xl:gap-8">
        {links.map((linkObject, index) => {
          if (linkObject.children && linkObject.children.length) {
            return (
              linkObject.link.title && (
                <li
                  className={`has-children cursor-pointer uppercase tracking-wider text-xxs xl:text-xs text-white relative inline-flex font-bold font-clash group items-center dropdown h-14 hover:text-accentRed`}
                  key={linkObject.link._key}
                  onClick={(e) => {
                    if (e.currentTarget == e.target) {
                      setState({...state, activePath: [linkObject]});
                    }
                  }}
                >
                  {linkObject.link.title}
                  <SubNavigation
                    parentLink={linkObject}
                    links={linkObject.children}
                    rootState={state}
                    setRootState={updateState}
                    currentDepth={0}
                    menuSide={menuSide}
                    mobile={mobile}
                    setIsOpen={setIsOpen}
                  />
                </li>
              )
            );
          }
          return (
            <li
              key={linkObject.link._key + index}
              onClick={() => {
                setState({
                  ...state,
                  activePath: [],
                });
              }}
              className="whitespace-pre cursor-pointer uppercase tracking-wider text-xxs xl:text-xs text-white font-bold font-clash group items-center relative dropdown inline-flex h-14 hover:text-accentRed"
            >
              {linkObject.link._type === 'linkInternal' && (
                <InternalLink
                  documentType={linkObject.link.documentType}
                  slug={linkObject.link.slug}
                  onClick={() => {
                    if (setIsOpen) {
                      setIsOpen(false);
                    }
                  }}
                >
                  {linkObject.link.title}
                </InternalLink>
              )}
              {linkObject.link._type === 'linkExternal' && (
                <a
                  href={linkObject.link.url}
                  target={linkObject.link.newWindow ? '_blank' : ''}
                  rel="noreferrer"
                  onClick={() => {
                    if (setIsOpen) {
                      setIsOpen(false);
                    }
                  }}
                >
                  {linkObject.link.title}
                </a>
              )}
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

function SubNavigation({
  parentLink,
  links,
  currentDepth,
  rootState,
  setRootState,
  menuSide,
  mobile = false,
  setIsOpen,
}: {
  parentLink: SanityPageMenuLinkItem;
  links: SanityPageMenuLinkItem[];
  currentDepth: number;
  rootState: pageMenuState;
  setRootState: (newState: pageMenuState) => void;
  menuSide: string;
  mobile: boolean;
  setIsOpen?: (isOpen: boolean) => void;
}) {
  const clearOpenMenus = () => {
    setRootState('' as any);
  };

  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event: {target: any}) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        clearOpenMenus();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [menuRef]);

  return (
    <Transition
      show={rootState?.activePath[currentDepth] === parentLink}
      className={
        menuSide === 'mobile-menu'
          ? 'fixed top-0 left-0 w-full h-full bg-opacity-50 z-50'
          : ''
      }
      enter="transition-opacity duration-200"
      enterFrom="opacity-0"
      enterTo="opacity-100"
      leave="transition-opacity duration-200"
      leaveFrom="opacity-100"
      leaveTo="opacity-0"
    >
      <ul
        ref={menuRef}
        className={`top-sub-menu-height right-sub-menu absolute sub-menu bg-white z-50 shadow-lg`}
      >
        {mobile && (
          <li
            className={`gap-2 normal-case tracking-normal flex top-item relative items-center font-inter cursor-pointer font-bold px-4 text-xxs xl:text-sm text-black has-children`}
            onClick={() => {
              const filteredActivePath = rootState.activePath.filter(
                (link, index) => index !== rootState.activePath.length - 1,
              );
              setRootState({
                ...rootState,
                activePath: filteredActivePath,
              });
            }}
          >
            <div>
              <IconChevron direction={'left'} />
            </div>
            {parentLink.link.title}
          </li>
        )}
        {parentLink.link.documentType === 'collection' &&
          parentLink.link._type === 'linkInternal' && (
            <InternalLink
              documentType={parentLink.link.documentType}
              slug={parentLink.link.slug}
              onClick={() => {
                if (setIsOpen) {
                  setIsOpen(false);
                }
              }}
            >
              <li
                className={`gap-2 flex tracking-normal relative normal-case items-center font-inter cursor-pointer font-normal px-4 text-xxs xl:text-sm text-black has-children`}
              >
                Näytä kaikki tuotteet
              </li>
            </InternalLink>
          )}

        {links.map((linkObject, index, row) => {
          if (linkObject.children) {
            return (
              <div key={linkObject.link._key}>
                <li
                  className={`gap-2 flex relative items-center tracking-normal font-inter normal-case cursor-pointer font-normal px-4 text-xxs xl:text-sm text-black has-children ${
                    index + 1 === row.length ? 'no-line' : ''
                  }`}
                  key={linkObject.link._key + index}
                  onClick={(e) => {
                    if (e.currentTarget == e.target) {
                      if (rootState.activePath[currentDepth + 1]) {
                        setRootState({
                          ...rootState,
                          activePath: Object.assign([], rootState.activePath, {
                            [currentDepth + 1]: linkObject,
                          }),
                        });
                      } else {
                        setRootState({
                          ...rootState,
                          activePath: [...rootState.activePath, linkObject],
                        });
                      }
                    }
                  }}
                >
                  {linkObject.link.title}

                  <div
                    onClick={(e) => {
                      if (rootState.activePath[currentDepth + 1]) {
                        setRootState({
                          ...rootState,
                          activePath: Object.assign([], rootState.activePath, {
                            [currentDepth + 1]: linkObject,
                          }),
                        });
                      } else {
                        setRootState({
                          ...rootState,
                          activePath: [...rootState.activePath, linkObject],
                        });
                      }
                    }}
                  >
                    <IconChevron />
                  </div>
                </li>
                <SubNavigation
                  parentLink={linkObject}
                  links={linkObject.children}
                  rootState={rootState}
                  setRootState={setRootState}
                  currentDepth={currentDepth + 1}
                  menuSide={menuSide}
                  mobile={mobile}
                  setIsOpen={setIsOpen}
                />
              </div>
            );
          }
          if (linkObject.link._type === 'linkInternal') {
            return (
              <InternalLink
                documentType={linkObject.link.documentType}
                slug={linkObject.link.slug}
                key={linkObject.link._key + index}
                onClick={() => {
                  if (setIsOpen) {
                    setIsOpen(false);
                  }
                }}
              >
                <li className="gap-2 flex relative tracking-normal normal-case items-center font-inter cursor-pointer font-normal px-4 text-xxs xl:text-sm text-black">
                  {linkObject.link.title}
                </li>
              </InternalLink>
            );
          }
          if (linkObject.link._type === 'linkExternal') {
            return (
              <a
                href={linkObject.link.url}
                target={linkObject.link.newWindow ? '_blank' : ''}
                rel="noreferrer"
                key={linkObject.link._key + index}
                onClick={() => {
                  if (setIsOpen) {
                    setIsOpen(false);
                  }
                }}
              >
                <li className="gap-2 flex relative tracking-normal items-center font-inter cursor-pointer font-normal px-4 text-xxs xl:text-sm text-black">
                  {linkObject.link.title}
                </li>
              </a>
            );
          }
        })}
      </ul>
    </Transition>
  );
}
